/**
 * CronLogsWorkspace — admin read-only view of scheduled-job runs.
 *
 * Lists the most recent `cron_log` rows (newest first): which job ran, whether
 * it succeeded, when it started, how long it took, and the one-line summary
 * (or the error on failure). Auto-refreshes every 30 s. Lets an admin confirm
 * the daily Slack digest / hourly servis reminders actually fired and what
 * they did — without shelling into the scheduler container.
 */

import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { Toolband } from '@/components/Toolband';
import { ApiError } from '@/lib/api';
import {
  useCronLogsQuery,
  useRunDailyDigestMutation,
  useTestDailyDigestMutation,
  type CronLog,
} from './api';

interface CronLogsWorkspaceProps {
  isAdmin: boolean;
}

const STATUS_TONE: Record<CronLog['status'], BadgeTone> = {
  ok: 'ok',
  error: 'danger',
  running: 'accent',
};

const STATUS_LABEL: Record<CronLog['status'], string> = {
  ok: 'OK',
  error: 'Chyba',
  running: 'Běží',
};

const JOB_LABEL: Record<string, string> = {
  'send-daily-digest': 'Denní přehled (Slack)',
  'send-reminders': 'Servisní připomínky (e-mail)',
  'platby-reconcile': 'Půjčovna: srovnání plateb s bránou',
  'pujcovna-uklid-prihlaseni': 'Půjčovna: úklid ověřovacích kódů',
};

const DATETIME = new Intl.DateTimeFormat('cs-CZ', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function formatRun(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  return DATETIME.format(d);
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function CronLogsWorkspace({ isAdmin }: CronLogsWorkspaceProps) {
  const query = useCronLogsQuery(isAdmin);
  const { confirm, dialog } = useConfirm();
  const testDigest = useTestDailyDigestMutation();
  const runDigest = useRunDailyDigestMutation();
  const busy = testDigest.isPending || runDigest.isPending;

  async function handleTest() {
    try {
      const r = await testDigest.mutateAsync();
      if (r.sent) {
        toast.ok(r.hadItems ? 'Test odeslán na váš Slack' : 'Test odeslán (dnes nemáte položky)');
      } else {
        toast.error(r.reason ?? 'Test se nepodařilo odeslat');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Test se nepodařilo odeslat');
    }
  }

  async function handleRun() {
    const ok = await confirm({
      title: 'Spustit denní přehled teď?',
      description:
        'Odešle Slack zprávu všem uživatelům, kteří mají na dnešek nebo po termínu nějaké položky. Stejné jako ranní cron v 9:00.',
      confirmLabel: 'Spustit teď',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const r = await runDigest.mutateAsync();
      toast.ok(`Hotovo — odesláno ${r.sent} z ${r.recipients} (chyby: ${r.errors})`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Spuštění se nepodařilo');
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Pouze pro administrátory"
          description="Záznamy naplánovaných úloh vidí jen administrátoři."
        />
      </div>
    );
  }

  const accessDenied =
    query.isError && query.error instanceof ApiError && query.error.status === 403;
  const logs = query.data?.logs ?? [];

  const columns: DataTableColumn<CronLog>[] = [
    {
      id: 'job',
      header: 'Úloha',
      width: 'minmax(200px, 1.2fr)',
      priority: 'primary',
      cell: (log) => <span className="text-[13px] text-ink">{JOB_LABEL[log.job] ?? log.job}</span>,
    },
    {
      id: 'stav',
      header: 'Stav',
      width: '110px',
      priority: 'meta',
      cell: (log) => <Badge tone={STATUS_TONE[log.status]}>{STATUS_LABEL[log.status]}</Badge>,
    },
    {
      id: 'spusteno',
      header: 'Spuštěno',
      width: '190px',
      priority: 'meta',
      cell: (log) => <span className="font-mono text-[11.5px] text-ink-2">{formatRun(log.started_at)}</span>,
    },
    {
      id: 'trvani',
      header: 'Trvání',
      width: '100px',
      priority: 'meta',
      cell: (log) => <span className="font-mono text-[11.5px] text-ink-2">{formatDuration(log.duration_ms)}</span>,
    },
    {
      id: 'vysledek',
      header: 'Výsledek',
      width: 'minmax(260px, 2fr)',
      priority: 'secondary',
      cell: (log) =>
        log.status === 'error' ? (
          <pre className="max-w-[640px] whitespace-pre-wrap break-words font-mono text-[11px] text-red-text">
            {log.error || 'Neznámá chyba'}
          </pre>
        ) : (
          <span className="font-mono text-[11.5px] text-ink-2">{log.summary || '—'}</span>
        ),
    },
  ];

  // Same shape as the other Platforma tabs: the toolband is the top edge of the tab (paper bg,
  // hairline, gutter — NOT a card inside the padded body), then the gray body with a bordered table.
  return (
    <div data-testid="cron-logs-workspace" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
          Naplánované úlohy · {logs.length} {logs.length === 1 ? 'záznam' : 'záznamů'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden font-mono text-[10.5px] text-ink-3 sm:inline">
            Auto-obnova 30 s
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="mail" size={12} />}
            disabled={busy}
            onClick={handleTest}
          >
            Poslat test mně
          </Button>
          <Button
            variant="ink"
            size="sm"
            icon={<Icon name="bell" size={12} />}
            disabled={busy}
            onClick={handleRun}
          >
            Spustit teď
          </Button>
        </div>
      </Toolband>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : accessDenied ? (
          <EmptyState eyebrow="403" title="Přístup odepřen" description="Vyžaduje administrátorská práva." />
        ) : query.isError ? (
          <EmptyState eyebrow="Chyba" title="Záznamy nelze načíst" description="Zkuste obnovit stránku." />
        ) : logs.length === 0 ? (
          <EmptyState
            title="Zatím žádné záznamy"
            description="Jakmile proběhne naplánovaná úloha, objeví se zde."
          />
        ) : (
          <DataTable<CronLog>
            aria-label="Záznamy naplánovaných úloh"
            variant="bordered"
            columns={columns}
            data={logs}
            getRowId={(log) => String(log.id)}
          />
        )}
      </div>
      {dialog}
    </div>
  );
}
